import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Heading } from "style/components/Heading.styles";
import {
  IdentityVerificationContainer,
  VerifiedStatus,
  VerificationItemRow,
  InfoSection,
  EmptyStateText,
} from "./IdentityVerification.styles";
import { useCheckAlpacaAccountStatus } from "features/Account/hook";
import { Button } from "components/Button/index";
import { IdentityVerificationModal } from "./IdentityVerificationModal";
import { CheckCircleIcon } from "lucide-react";
import { DOCUMENT_CONFIG, DOCUMENT_TYPES } from "./constants";

export const IdentityVerification = ({ hideWhenNothingToDo = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const { data: alpacaStatus, isLoading } = useCheckAlpacaAccountStatus();

  const isVerified = alpacaStatus?.approved;
  // only managing three documents for now
  const requiredDocuments = useMemo(() => {
    if (!alpacaStatus) return [];

    const required = [];

    if (
      alpacaStatus[DOCUMENT_TYPES.IDENTITY_VERIFICATION] ||
      alpacaStatus[DOCUMENT_TYPES.DATE_OF_BIRTH_VERIFICATION]
    ) {
      required.push(DOCUMENT_CONFIG[DOCUMENT_TYPES.IDENTITY_VERIFICATION]);
    }

    if (alpacaStatus[DOCUMENT_TYPES.ADDRESS_VERIFICATION]) {
      required.push(DOCUMENT_CONFIG[DOCUMENT_TYPES.ADDRESS_VERIFICATION]);
    }

    if (alpacaStatus[DOCUMENT_TYPES.TAX_ID_VERIFICATION]) {
      required.push(DOCUMENT_CONFIG[DOCUMENT_TYPES.TAX_ID_VERIFICATION]);
    }

    if (alpacaStatus.affiliation_approval) {
      required.push(DOCUMENT_CONFIG[DOCUMENT_TYPES.ACCOUNT_APPROVAL_LETTER]);
      required.push(DOCUMENT_CONFIG[DOCUMENT_TYPES.COMPANY_FORMATION]);
    }

    return required;
  }, [alpacaStatus]);

  // On the Privacy page the section only appears when documents are needed.
  if (
    hideWhenNothingToDo &&
    (isLoading || isVerified || requiredDocuments.length === 0)
  ) {
    return null;
  }

  const handleUploadClick = (docType) => {
    setSelectedDocument(docType);
    setShowModal(true);
  };

  return (
    <IdentityVerificationContainer>
      <section>
        <div className="heading-container">
          <div className="heading-wrapper">
            <Heading>Account verification</Heading>
            <span>
              {isVerified
                ? "Your account has been successfully verified."
                : "Upload the required documents to verify your account."}
            </span>
          </div>
        </div>

        <div className="documents-list">
          {isVerified ? (
            <div className="button-wrapper">
              <VerifiedStatus>
                <CheckCircleIcon size={20} />
                Account verified
              </VerifiedStatus>
            </div>
          ) : isLoading ? (
            <span>Checking status...</span>
          ) : (
            <>
              {requiredDocuments.map((doc) => (
                <VerificationItemRow key={doc.id}>
                  <InfoSection>
                    <strong>{doc.title}</strong>
                    <span>{doc.description}</span>
                  </InfoSection>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => handleUploadClick(doc.id)}
                  >
                    Upload
                  </Button>
                </VerificationItemRow>
              ))}
              {requiredDocuments.length === 0 && !isVerified && (
                <EmptyStateText>
                  No specific documents required at this time.
                </EmptyStateText>
              )}
            </>
          )}
        </div>
      </section>

      <IdentityVerificationModal
        show={showModal}
        onHide={() => setShowModal(false)}
        requiredDocumentType={selectedDocument}
      />
    </IdentityVerificationContainer>
  );
};

IdentityVerification.propTypes = {
  hideWhenNothingToDo: PropTypes.bool,
};
